// Shared form primitives used throughout the app.
import React from "react";
import { AlertCircle, Check, ChevronDown } from "lucide-react";

/*  UI PRIMITIVES                                                       */
/* ------------------------------------------------------------------ */

export const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 shadow-sm outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100";
export const inputErrCls = "w-full rounded-lg border border-rose-300 bg-rose-50/40 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 shadow-sm outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100";

export interface FieldProps {
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
  error?: string;
}
export function Field({ label, required, children, hint, error }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline gap-1 text-sm font-medium text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
      {error && <span className="mt-1 flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3" />{error}</span>}
    </label>
  );
}

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
}
export function TextInput({ error, ...props }: TextInputProps) {
  return <input {...props} className={error ? inputErrCls : inputCls} />;
}

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
}
export function Textarea({ error, ...props }: TextareaProps) {
  return <textarea {...props} className={(error ? inputErrCls : inputCls) + " min-h-[90px] resize-y"} />;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: string;
  children: React.ReactNode;
}
export function Select({ error, children, ...props }: SelectProps) {
  return (
    <div className="relative">
      <select {...props} className={(error ? inputErrCls : inputCls) + " appearance-none pr-8"}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

export interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}
export function Checkbox({ label, checked, onChange }: CheckboxProps) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
      <span
        onClick={() => onChange(!checked)}
        className={`flex h-4.5 w-4.5 items-center justify-center rounded border transition ${checked ? "border-cyan-600 bg-cyan-600" : "border-slate-300 bg-white"}`}
      >
        {checked && <Check className="h-3 w-3 text-white" />}
      </span>
      {label}
    </label>
  );
}

export interface SectionHeadingProps {
  title: string;
  subtitle?: string;
}
export function SectionHeading({ title, subtitle }: SectionHeadingProps) {
  return (
    <div className="mb-5">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
    </div>
  );
}

